//------------------------------------------------------------------------------
// 共通レイアウト - ヘッダー
//------------------------------------------------------------------------------
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import appConfig from '@/config';
import theme from '@/Theme';
import { setSidebarOpen } from '@/reducks/sidebar';

// 型定義
type MenuEvent = React.MouseEvent<HTMLElement>;

/**
 * ヘッダー
 */
export default function Header() {

  // メニュー表示状態
  const [
    menuAnchor,
    setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const handleMenuOpen = (e: MenuEvent) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null); 

  // アプリバー
  return (
    <AppBar
      sx={{
        flexGrow: 0,
        position: 'static',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: 'white',
        borderBottomSize: '3px',
        borderBottomStyle: 'solid',

        // @ts-ignore 境界線はロゴ色
        borderBottomColor: theme.logo.main,
      }}
    >
      {/* ツールバー */}
      <Toolbar variant="dense">

        {/* サイドバー表示ボタン */}
        <SidebarButton />

        {/* ロゴ */}
        <Box component="img" src="/logo.svg" alt=""
          sx={{
            mr: 1,
            height: 24,
            background: 'white',
          }}
        />

        {/* タイトル (システム名) */}
        <Typography
          component="div"
          variant="subtitle1"
          noWrap
          sx={{
            px: 2,
            height: '100%',
            display: 'flex',
            alignItems: 'center',

            // @ts-ignore 背景はロゴ色
            background: theme.logo.main,
            fontWeight: 'bold',
          }}
        >
          {appConfig.sysTitle}
        </Typography>

        {/* スペーサー */}
        <Box sx={{ flexGrow: 1 }} />

        {/* ユーザー情報メニュー */}
        <UserButton
          menuAnchor={menuAnchor}
          handleMenuOpen={handleMenuOpen}
          handleMenuClose={handleMenuClose}
        />
      </Toolbar>
    </AppBar>
  );
}

// サイドバー開閉ボタン
function SidebarButton() {

  // サイドバー開閉
  const dispatch = useDispatch();
  const sidebarOpen = useSelector<any, any>((state) => state.sidebar.open);
  const toggleSidebar = () => dispatch(setSidebarOpen(!sidebarOpen));

  // アイコンボタン
  return (
    <IconButton
      aria-label="サイドメニュー"
      color="inherit"
      edge="start"
      sx={{ color: 'black' }}
      onClick={toggleSidebar}
    >
      {/* メニューアイコン */}
      <MenuIcon />
    </IconButton>
   );
}

// メニュー項目
const userMenuItems = [
  {
    text: 'ユーザー情報',
    icon: <ManageAccountsIcon />,
    link: ''
  }, {
    text: 'ログアウト',
    icon: <LogoutIcon />,
    link: appConfig.BACK_TO_LOGIN,
  },
]

// ユーザー情報ボタン
function UserButton({
  menuAnchor,
  handleMenuOpen,
  handleMenuClose,
}: {
  menuAnchor?: HTMLElement | null,
  handleMenuOpen: (e: MenuEvent) => void,
  handleMenuClose: () => void,
}) {

  // ユーザー情報
  const loginUser = useSelector<any, any>((state) => state.auth.userInfo);

  // メニュー項目ハンドラー
  const navigate = useNavigate();
  const handleClick = (link: string) => {
    handleMenuClose();
    if (link) {
      navigate(link);
    }
  };
  const menuOpen = Boolean(menuAnchor);
  return (
    <>
      <IconButton
        aria-label="ユーザー情報"
        color="inherit"
        edge="end"
        sx={{ ml: 1, color: 'black' }}
        onClick={handleMenuOpen}
      >
        <Typography
          variant="button"
          component="span"
          sx={{ ml: 1, mr: 1 }}>
          {loginUser?.userName}
        </Typography>
        <AccountCircle />
      </IconButton>

      {/* ユーザー情報メニュー */}
      <Menu
        id="user-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
      > {
        userMenuItems.map((item) => (
          <MenuItem
            key={item.text}
            onClick={() => handleClick(item.link)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
